import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Inbox, RefreshCw, RotateCcw, XCircle, Trash2, Settings as SettingsIcon, AlertCircle, CheckCircle, Clock, Zap, Loader2 } from "lucide-react";
import { fmtLog } from "@/lib/tz";

const TEAL = "#0f766e";

const PRIORITY_COLORS = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  normal: "bg-blue-50 text-blue-700 border-blue-200",
  low: "bg-slate-100 text-slate-500 border-slate-200",
};

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  sending: "bg-blue-50 text-blue-700 border-blue-200",
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

function StatsCard({ stats, onRefresh, loading }) {
  if (!stats) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5" style={{ color: TEAL }} />
          <h2 className="text-base font-semibold text-slate-800">Queue Status</h2>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="text-center p-3 rounded-lg bg-amber-50 border border-amber-100">
          <div className="text-2xl font-bold text-amber-700">{stats.pending || 0}</div>
          <div className="text-xs text-amber-600 mt-1">Pending</div>
          {stats.pending_high > 0 && (
            <div className="text-[10px] text-rose-600 mt-1 font-semibold">{stats.pending_high} high priority</div>
          )}
        </div>

        <div className="text-center p-3 rounded-lg bg-blue-50 border border-blue-100">
          <div className="text-2xl font-bold text-blue-700">{stats.sending || 0}</div>
          <div className="text-xs text-blue-600 mt-1">Sending</div>
        </div>

        <div className="text-center p-3 rounded-lg bg-emerald-50 border border-emerald-100">
          <div className="text-2xl font-bold text-emerald-700">{stats.sent_24h || 0}</div>
          <div className="text-xs text-emerald-600 mt-1">Sent (24h)</div>
        </div>

        <div className="text-center p-3 rounded-lg bg-rose-50 border border-rose-100">
          <div className="text-2xl font-bold text-rose-700">{stats.failed || 0}</div>
          <div className="text-xs text-rose-600 mt-1">Failed</div>
        </div>
      </div>

      {stats.settings && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-slate-500">
              Queue: <span className={`font-semibold ${stats.settings.enabled ? "text-emerald-600" : "text-slate-400"}`}>
                {stats.settings.enabled ? "Enabled" : "Disabled"}
              </span>
            </span>
            <span className="text-slate-500">
              Delay: <span className="font-semibold text-slate-700">{stats.settings.send_delay_seconds}s</span>
            </span>
            <span className="text-slate-500">
              Max retries: <span className="font-semibold text-slate-700">{stats.settings.max_attempts}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function PendingTab({ onRefresh }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.emailQueue.getPending({ limit: 200 });
      setPending(data.pending || []);
    } catch (e) {
      toast.error("Failed to load pending emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this email? It will be marked as failed.")) return;
    setActionLoading(id);
    try {
      await api.emailQueue.cancel(id);
      toast.success("Email cancelled");
      load();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...</div>;

  if (pending.length === 0) {
    return (
      <div className="p-8 text-center">
        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
        <p className="text-sm text-slate-500">No pending emails</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {pending.map((item) => (
        <div key={item.id} className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`border ${PRIORITY_COLORS[item.priority]}`}>
                  {item.priority === "high" && <Zap className="h-3 w-3 mr-1" />}
                  {item.priority}
                </Badge>
                <span className="text-xs text-slate-400">{item.key || "unknown"}</span>
              </div>
              <p className="text-sm font-medium text-slate-800 truncate">{item.subject}</p>
              <p className="text-xs text-slate-500 mt-1">To: {item.to}</p>
              <p className="text-[11px] text-slate-400 mt-1">
                Scheduled: {fmtLog(item.scheduled_for)} · Attempts: {item.attempts}/{item.max_attempts}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-rose-600"
              onClick={() => cancel(item.id)}
              disabled={actionLoading === item.id}
            >
              {actionLoading === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}



function FailedTab({ onRefresh }) {
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.emailQueue.getFailed({ limit: 200 });
      setFailed(data.failed || []);
    } catch (e) {
      toast.error("Failed to load failed emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    setActionLoading(id);
    try {
      await api.emailQueue.retry(id);
      toast.success("Email queued for retry");
      load();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry");
    } finally {
      setActionLoading(null);
    }
  };

  const retryAll = async () => {
    if (!window.confirm(`Retry all ${failed.length} failed emails?`)) return;
    setBulkLoading(true);
    try {
      const res = await api.emailQueue.retryAllFailed();
      toast.success(res.message || "All emails queued for retry");
      load();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry all");
    } finally {
      setBulkLoading(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Permanently delete all ${failed.length} failed emails? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      const res = await api.emailQueue.clearFailed();
      toast.success(res.message || "All failed emails deleted");
      load();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear");
    } finally {
      setBulkLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...</div>;

  return (
    <div>
      {failed.length > 0 && (
        <div className="flex gap-2 mb-4">
          <Button size="sm" variant="outline" onClick={retryAll} disabled={bulkLoading}>
            <RotateCcw className="h-4 w-4 mr-1.5" />Retry all ({failed.length})
          </Button>
          <Button size="sm" variant="outline" className="text-rose-600" onClick={clearAll} disabled={bulkLoading}>
            <Trash2 className="h-4 w-4 mr-1.5" />Clear all
          </Button>
        </div>
      )}

      {failed.length === 0 ? (
        <div className="p-8 text-center">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
          <p className="text-sm text-slate-500">No failed emails</p>
        </div>
      ) : (
        <div className="space-y-2">
          {failed.map((item) => (
            <div key={item.id} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="border bg-rose-50 text-rose-700 border-rose-200">
                      <AlertCircle className="h-3 w-3 mr-1" />Failed
                    </Badge>
                    <span className="text-xs text-slate-400">{item.key || "unknown"}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800 truncate">{item.subject}</p>
                  <p className="text-xs text-slate-500 mt-1">To: {item.to}</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Last attempt: {fmtLog(item.last_attempt_at)} · Attempts: {item.attempts}/{item.max_attempts}
                  </p>
                  {item.error && (
                    <p className="text-xs text-rose-600 mt-2 font-mono bg-rose-50 p-2 rounded">{item.error}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => retry(item.id)}
                  disabled={actionLoading === item.id}
                >
                  {actionLoading === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




function SettingsDialog({ open, onClose, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.emailQueue.getSettings().then(setSettings).catch(() => toast.error("Failed to load settings"));
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.emailQueue.updateSettings(settings);
      toast.success("Settings saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Email Queue Settings</DialogTitle>
          <DialogDescription>
            Control how the email queue processes outbound emails.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Queue enabled</Label>
              <p className="text-xs text-slate-500 mt-0.5">When off, emails send immediately (no queue)</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Send delay (seconds)</Label>
            <p className="text-xs text-slate-500 mb-2">Time between each email (prevents SMTP overload)</p>
            <Select
              value={String(settings.send_delay_seconds)}
              onValueChange={(v) => setSettings({ ...settings, send_delay_seconds: parseInt(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 seconds</SelectItem>
                <SelectItem value="7">7 seconds</SelectItem>
                <SelectItem value="10">10 seconds (recommended)</SelectItem>
                <SelectItem value="15">15 seconds</SelectItem>
                <SelectItem value="20">20 seconds</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium">Max retry attempts</Label>
            <p className="text-xs text-slate-500 mb-2">How many times to retry failed emails</p>
            <Select
              value={String(settings.max_attempts)}
              onValueChange={(v) => setSettings({ ...settings, max_attempts: parseInt(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 attempt (no retries)</SelectItem>
                <SelectItem value="2">2 attempts</SelectItem>
                <SelectItem value="3">3 attempts (recommended)</SelectItem>
                <SelectItem value="5">5 attempts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ backgroundColor: TEAL }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function EmailQueue() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [tab, setTab] = useState("pending");

  const loadStats = async () => {
    try {
      const data = await api.emailQueue.getStats();
      setStats(data);
    } catch (e) {
      toast.error("Failed to load queue stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Inbox className="h-5 w-5" style={{ color: TEAL }} />
            <h1 className="text-xl font-semibold font-brand text-slate-800">Email Queue</h1>
          </div>
          <p className="text-sm text-slate-500">
            View and manage queued, failed, and sent emails. Authentication emails get high priority.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowSettings(true)}>
          <SettingsIcon className="h-4 w-4 mr-1.5" />
          Settings
        </Button>
      </div>

      <StatsCard stats={stats} onRefresh={loadStats} loading={loading} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending">
            Pending {stats?.pending > 0 && `(${stats.pending})`}
          </TabsTrigger>
          <TabsTrigger value="failed">
            Failed {stats?.failed > 0 && `(${stats.failed})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <PendingTab onRefresh={loadStats} />
        </TabsContent>

        <TabsContent value="failed">
          <FailedTab onRefresh={loadStats} />
        </TabsContent>
      </Tabs>

      <SettingsDialog open={showSettings} onClose={() => setShowSettings(false)} onSaved={loadStats} />
    </div>
  );
}
